import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Form, Formik, FormikConfig } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { SkillType } from '../../../API';
import { updateEngineer } from '../../../models/engineer';
import { useAuth } from '../../Authentication';
import ButtonWithLoading from '../../ButtonWithLoading';
import {
  ExperienceField,
  FileField,
  SkillsAutocompleteField,
  TextField,
} from '../../Fields';
import Loading from '../../Loading';
import useEngineerWithSkills from '../useEngineerWithSkills';
import useResumeAttachment from '../useResumeAttachment';

interface Props {
  open: boolean;
  onClose: (changed?: boolean) => void;
}

const engineerEditSchema = Yup.object().shape(
  {
    experience: Yup.string().required('Experience is required'),
    skills: Yup.array().min(1, 'Please select at least one main technology'),
    linkedIn: Yup.string()
      .url()
      .when('cvFile', {
        is: (file: any) => !file || file.length === 0,
        then: Yup.string()
          .url()
          .required('Please submit either your LinkedIn or resume'),
        otherwise: Yup.string().url('It needs to be a valid URL'),
      }),
    cvFile: Yup.mixed().when('linkedIn', {
      is: (linkedIn: string) => linkedIn?.length > 0,
      then: Yup.mixed(),
      otherwise: Yup.mixed().test({
        test: (file: any) => file?.key || file?.size > 0,
        message: 'Please submit either your LinkedIn or resume',
      }),
    }),
  },
  [['linkedIn', 'cvFile']],
);

export default function EditProfileDialog({ open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { engineer } = useEngineerWithSkills();
  const [cvUrl, setCvUrl] = useState<string | undefined>();
  const { getDownloadUrl, upload } = useResumeAttachment(engineer);
  useEffect(() => {
    getDownloadUrl().then((url) => url && setCvUrl(url));
  }, [getDownloadUrl]);

  if (!engineer) return <Loading />;

  const initialValues = {
    experience: engineer.experience,
    skills: engineer.skills || [],
    linkedIn: engineer.linkedIn || '',
    cvFile: engineer.cv,
  };
  const handleClose = () => {
    onClose();
  };
  const handleSubmit: FormikConfig<typeof initialValues>['onSubmit'] = async ({
    experience,
    skills,
    linkedIn,
    cvFile,
  }) => {
    try {
      const s3Object = cvFile instanceof File
        ? await upload(user!.identityId, cvFile)
        : undefined;
      await updateEngineer({
        id: engineer.id,
        linkedIn,
        cv: s3Object,
        experience,
        skillIds: skills.map((skill) => skill.id),
      });
      enqueueSnackbar('Profile updated', { variant: 'success' });
      onClose(true);
    } catch (err) {
      enqueueSnackbar(
        "We couldn't update your profile, please try again later",
        { variant: 'error' },
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      aria-labelledby="form-dialog-title"
      fullWidth
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={engineerEditSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogTitle id="form-dialog-title">Edit Profile</DialogTitle>
            <DialogContent>
              <ExperienceField
                name="experience"
                label="Years of Experience"
                helperText="Years of relevant software development experience"
                fullWidth
                margin="normal"
                autoFocus
              />
              <SkillsAutocompleteField
                label="Technologies"
                helperText="You are proficient in these technologies"
                name="skills"
                type={SkillType.MAIN}
                fullWidth
                margin="normal"
              />
              <TextField
                name="linkedIn"
                label="LinkedIn URL"
                fullWidth
                margin="normal"
              />
              <FileField
                name="cvFile"
                label="Resume file"
                fullWidth
                margin="normal"
                currentFileUrl={cvUrl}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <ButtonWithLoading
                type="submit"
                color="primary"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Save
              </ButtonWithLoading>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
